import moment from 'moment';

import { __t, applyTranslations } from './language';

applyTranslations(require('./i18n/date-util.i18n.json'));

moment.updateLocale('en', {
  week: {
    dow: 1, // week stars from Monday
    doy: moment.localeData().firstDayOfYear()
  }
});

// @todo: use "L" <- needs a global locale handling config
// @see: http://momentjs.com/docs/#/i18n/
export const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';
export const DISPLAY_DATE_FORMAT_NO_YEAR = 'DD/MM';
export const DISPLAY_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';
export const DISPLAY_TIME_FORMAT = 'HH:mm';
export const HTML_DATE_FORMAT = 'YYYY-MM-DD';
export const DB_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY hh:mm:ss';

export const MIN_USER_AGE = 13;
export const MAX_USER_AGE = 100;

export const DELIMITER_OVERRIDE = '–';

// eslint-disable-next-line
type MomentDateInput = any;

export function getHTMLDate(date: MomentDateInput): string | undefined | null {
  return date ? moment(date).format(HTML_DATE_FORMAT) : null;
}

export function getDisplayDate(date: MomentDateInput): string {
  return date ? moment(date).format(DISPLAY_DATE_FORMAT) : '';
}

export function getDisplayDateNoYear(date: MomentDateInput): string {
  return date ? moment(date).format(DISPLAY_DATE_FORMAT_NO_YEAR) : '';
}

export function isToday(date: MomentDateInput): boolean {
  const today = moment();
  return moment(date).diff(today, 'day') === 0;
}

// Function output:
// same day format: 01/01/2000 18:00
// next day format: 01/01/2000
export function getDisplayDateAndTime(date: MomentDateInput): string {
  if (!date) {
    return '';
  }

  if (moment(date).isSame(new Date(), 'day')) {
    return moment(date).format(DISPLAY_TIME_FORMAT);
  }

  return moment(date).format(DISPLAY_DATETIME_FORMAT);
}

export function isOverdue(endDate: MomentDateInput) {
  const today = (new Date()).setHours(0, 0, 0, 0);
  return moment(endDate).isBefore(today);
}

// Function output:
// same day format: 18:00
// next day format: 01/01/2000
export function getTruncatedDisplayDateAndTime(date: MomentDateInput): string {
  if (!date) {
    return '';
  }

  if (moment(date).isSame(new Date(), 'day')) {
    return moment(date).format(DISPLAY_TIME_FORMAT);
  }

  return moment(date).format(DISPLAY_DATE_FORMAT);
}

export function getDiffInDays(date: MomentDateInput): string {
  const thatDate = moment(date);
  const today = moment().set({
    hour: thatDate.get('hour'),
    minute: thatDate.get('minute'),
    second: thatDate.get('second'),
    millisecond: thatDate.get('millisecond')
  });

  const days = thatDate.diff(today, 'days');

  if (days < 1) {
    return __t('date_util.in_zero_days');
  }

  if (days === 1) {
    return __t('date_util.in_one_day');
  }

  return __t('date_util.in_several_days', { number: days });
}

export function convertDateToISOFormat(date: string): string {
  if (!date) {
    return '';
  }

  const parts = date.split('/');

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

type DropdownOptions = {
  start: MomentDateInput,
  end: MomentDateInput,
  interval: moment.unitOfTime.Diff,
  format: string
};

export function getDateDropdownValues(options: DropdownOptions) {
  const dates: Array<string> = [];
  const startDate = moment(options.start);
  const endDate = moment(options.end);
  const flag = 'M';

  const numDatesToShow = endDate.diff(startDate, options.interval);

  for (let i = 0; i <= numDatesToShow; i++) {
    const date = moment(startDate).add(i, flag);
    dates.push(date.format(options.format));
  }

  return dates;
}

// Example: campaign dates
export function getDisplayDatesInterval(
  startDate: MomentDateInput,
  endDate: MomentDateInput,
  delimeterOverride: string = ''
): string {
  const startDatePreview = getDisplayDate(startDate);
  const endDatePreview = getDisplayDate(endDate);
  const delimeter = (startDatePreview && endDatePreview) ? ' - ' : delimeterOverride;

  return startDatePreview + delimeter + endDatePreview;
}

// Example: deliverable dates
export function getDisplayDateRange(startDate: MomentDateInput, endDate: MomentDateInput) {
  const from = getDisplayDate(startDate);
  const to = getDisplayDate(endDate);

  return from === to ? from : `${from} - ${to}`;
}

export function isLessThanNMinutesOld(date: string, minutes: number) {
  return moment().subtract(minutes, 'minutes').isBefore(date);
}

export function getDateDiffInYears(date1: MomentDateInput, date2: MomentDateInput): number {
  return moment(date1).diff(moment(date2), 'years');
}

export function getDateDiffInDays(date1: MomentDateInput, date2: MomentDateInput): number {
  return moment(date1).diff(moment(date2), 'days');
}

export function isTimeRangeIntersects(
  rangeAStart: string | undefined | null,
  rangeAEnd: string | undefined | null,
  rangeBStart: string,
  rangeBEnd: string
): boolean {
  // if range A start date not set - includes all dates in the past
  const rangeAStartValue = (rangeAStart && moment(rangeAStart).valueOf()) || -Infinity;
  // if range A end date not set - includes all dates in the future
  const rangeAEndValue = (rangeAEnd && moment(rangeAEnd).valueOf()) || Infinity;
  const rangeBStartValue = moment(rangeBStart).valueOf();
  const rangeBEndValue = moment(rangeBEnd).valueOf();

  return rangeAEndValue >= rangeBStartValue
    && rangeAStartValue <= rangeBEndValue;
}
